import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Home, LogOut, RefreshCw, TrendingUp, DollarSign,
  Target, Users, Clock, Mail, ArrowRight, BarChart3,
  CheckCircle, XCircle, AlertTriangle, Calendar,
  FileText, Send, Award
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [kpiData, setKpiData] = useState(null);
  const [salesKpi, setSalesKpi] = useState(null);
  const [loading, setLoading] = useState(true);

  // Auth
  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const verifyAuth = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/login');
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/verify`, getAuthHeaders());
      setUser(response.data);
    } catch {
      localStorage.removeItem('admin_token');
      navigate('/login');
    }
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    navigate('/login');
  };

  // Data fetching
  const fetchKPI = async () => {
    try {
      setLoading(true);
      const [kpiResponse, roiResponse, salesResponse] = await Promise.all([
        axios.get(`${API}/dashboard/kpi`, getAuthHeaders()),
        axios.get(`${API}/dashboard/marketing-roi`, getAuthHeaders()),
        axios.get(`${API}/dashboard/sales-kpi`, getAuthHeaders()).catch(() => ({ data: null }))
      ]);
      setKpiData({...kpiResponse.data, marketing_roi: roiResponse.data});
      setSalesKpi(salesResponse.data);
    } catch (error) {
      console.error('Erreur chargement KPI:', error);
      toast.error('Erreur lors du chargement des KPI');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    verifyAuth();
  }, []);

  useEffect(() => {
    if (user) {
      fetchKPI();
    }
  }, [user]);

  const formatCurrency = (value) => {
    if (!value) return '$0';
    return new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(value);
  };

  const formatPercent = (value) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  if (loading && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F5F5F5' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: '#335C2E' }}></div>
          <p className="mt-4" style={{ color: '#666666' }}>Chargement...</p>
        </div>
      </div>
    );
  }

  const stages = kpiData?.deals_by_stage || {};
  const conversions = kpiData?.conversion_rates || {};

  return (
    <div className="min-h-screen" style={{ background: '#F5F5F5' }}>
      {/* Header */}
      <header className="shadow-sm" style={{ background: '#FFFFFF' }}>
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/logo.png" alt="Refacing VR" className="h-10" onError={(e) => e.target.style.display = 'none'} />
            <h1 className="text-xl font-bold" style={{ color: '#335C2E' }}>Dashboard KPI</h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin')} className="p-2 rounded hover:bg-gray-100" title="Admin">
              <Home size={20} style={{ color: '#666666' }} />
            </button>
            <button onClick={() => navigate('/admin/pipeline')} className="p-2 rounded hover:bg-gray-100" title="Pipeline">
              <BarChart3 size={20} style={{ color: '#666666' }} />
            </button>
            <button onClick={fetchKPI} className="p-2 rounded hover:bg-gray-100" title="Rafraîchir">
              <RefreshCw size={20} style={{ color: '#666666' }} />
            </button>
            <span className="text-sm" style={{ color: '#666666' }}>{user?.username}</span>
            <button onClick={logout} className="p-2 rounded hover:bg-gray-100">
              <LogOut size={20} style={{ color: '#DC3545' }} />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* KPI Cards Row 1 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Valeur Pipeline Pondérée */}
          <div className="bg-white rounded-lg p-4 shadow-sm border-l-4" style={{ borderLeftColor: '#C7A027' }}>
            <div className="flex items-center gap-2 text-sm mb-1" style={{ color: '#666666' }}>
              <DollarSign size={16} />
              Valeur Pipeline Pondérée
            </div>
            <div className="text-2xl font-bold" style={{ color: '#C7A027' }}>
              {formatCurrency(kpiData?.weighted_pipeline_value)}
            </div>
            <p className="text-xs mt-1" style={{ color: '#999999' }}>Valeur × Probabilité</p>
          </div>

          {/* Cycle Time */}
          <div className="bg-white rounded-lg p-4 shadow-sm border-l-4" style={{ borderLeftColor: '#1976D2' }}>
            <div className="flex items-center gap-2 text-sm mb-1" style={{ color: '#666666' }}>
              <Clock size={16} />
              Cycle Moyen
            </div>
            <div className="text-2xl font-bold" style={{ color: '#1976D2' }}>
              {kpiData?.avg_cycle_time_days || 0} jours
            </div>
            <p className="text-xs mt-1" style={{ color: '#999999' }}>De lead à gagné</p>
          </div>

          {/* Deals Gagnés */}
          <div className="bg-white rounded-lg p-4 shadow-sm border-l-4" style={{ borderLeftColor: '#2E7D32' }}>
            <div className="flex items-center gap-2 text-sm mb-1" style={{ color: '#666666' }}>
              <CheckCircle size={16} />
              Deals Gagnés
            </div>
            <div className="text-2xl font-bold" style={{ color: '#2E7D32' }}>
              {(stages.won?.count || 0) + (stages.production?.count || 0) + (stages.delivered?.count || 0)}
            </div>
            <p className="text-xs mt-1" style={{ color: '#999999' }}>
              Valeur: {formatCurrency((stages.won?.value || 0) + (stages.production?.value || 0) + (stages.delivered?.value || 0))}
            </p>
          </div>

          {/* Automatisations en attente */}
          <div className="bg-white rounded-lg p-4 shadow-sm border-l-4" style={{ borderLeftColor: '#E65100' }}>
            <div className="flex items-center gap-2 text-sm mb-1" style={{ color: '#666666' }}>
              <Mail size={16} />
              Automatisations
            </div>
            <div className="text-2xl font-bold" style={{ color: '#E65100' }}>
              {kpiData?.pending_automation_tasks || 0}
            </div>
            <p className="text-xs mt-1" style={{ color: '#999999' }}>Tâches en attente</p>
          </div>
        </div>

        {/* Conversion Funnel */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: '#333333' }}>
              <TrendingUp size={18} />
              Funnel de Conversion
            </h3>
            <div className="space-y-4">
              {/* Lead → Qualified */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm">Lead → Qualifié</span>
                  <span className="font-bold" style={{ color: conversions.lead_to_qualified >= 30 ? '#2E7D32' : '#E65100' }}>
                    {formatPercent(conversions.lead_to_qualified)}
                  </span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full transition-all" 
                    style={{ 
                      width: `${Math.min(conversions.lead_to_qualified || 0, 100)}%`,
                      background: conversions.lead_to_qualified >= 30 ? '#81C784' : '#FFB74D'
                    }}
                  ></div>
                </div>
              </div>

              {/* Qualified → Estimate */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm">Qualifié → Soumission</span>
                  <span className="font-bold" style={{ color: conversions.qualified_to_estimate >= 50 ? '#2E7D32' : '#E65100' }}>
                    {formatPercent(conversions.qualified_to_estimate)}
                  </span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full transition-all" 
                    style={{ 
                      width: `${Math.min(conversions.qualified_to_estimate || 0, 100)}%`,
                      background: conversions.qualified_to_estimate >= 50 ? '#81C784' : '#FFB74D'
                    }}
                  ></div>
                </div>
              </div>

              {/* Estimate → Won */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm">Soumission → Gagné</span>
                  <span className="font-bold" style={{ color: conversions.estimate_to_won >= 25 ? '#2E7D32' : '#E65100' }}>
                    {formatPercent(conversions.estimate_to_won)}
                  </span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full transition-all" 
                    style={{ 
                      width: `${Math.min(conversions.estimate_to_won || 0, 100)}%`,
                      background: conversions.estimate_to_won >= 25 ? '#81C784' : '#FFB74D'
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Deals par Stage */}
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: '#333333' }}>
              <Target size={18} />
              Deals par Stage
            </h3>
            <div className="space-y-2">
              {['lead', 'qualified', 'estimate', 'negotiation', 'won', 'production', 'delivered', 'lost'].map(stage => {
                const data = stages[stage] || { count: 0, value: 0 };
                const stageLabels = {
                  lead: 'Lead', qualified: 'Qualifié', estimate: 'Soumission',
                  negotiation: 'Négociation', won: 'Gagné', production: 'Production',
                  delivered: 'Livré', lost: 'Perdu'
                };
                const colors = {
                  lead: '#64B5F6', qualified: '#4DB6AC', estimate: '#FFB74D',
                  negotiation: '#BA68C8', won: '#81C784', production: '#4FC3F7',
                  delivered: '#2E7D32', lost: '#E57373'
                };
                
                return (
                  <div key={stage} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full" style={{ background: colors[stage] }}></span>
                      <span>{stageLabels[stage]}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-bold">{data.count}</span>
                      <span className="text-xs" style={{ color: '#999999' }}>{formatCurrency(data.value)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top Sources */}
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: '#333333' }}>
              <Users size={18} />
              Top Sources
            </h3>
            {kpiData?.top_sources?.length > 0 ? (
              <div className="space-y-3">
                {kpiData.top_sources.slice(0, 5).map((source, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{source.source || 'Inconnu'}</p>
                      <p className="text-xs" style={{ color: '#999999' }}>
                        {source.total_deals} deals | {source.won_deals} gagnés
                      </p>
                    </div>
                    <span 
                      className="px-2 py-1 rounded text-xs font-bold"
                      style={{ 
                        background: source.conversion_rate >= 25 ? '#E8F5E9' : '#FFF3E0',
                        color: source.conversion_rate >= 25 ? '#2E7D32' : '#E65100'
                      }}
                    >
                      {formatPercent(source.conversion_rate)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-center py-4" style={{ color: '#999999' }}>Aucune donnée</p>
            )}
          </div>
        </div>

        {/* UTM Attribution & Recent Deals */}
        <div className="grid grid-cols-2 gap-4">
          {/* UTM Attribution */}
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: '#333333' }}>
              <Mail size={18} />
              Attribution Marketing (UTM)
            </h3>
            {kpiData?.submissions_by_utm?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">Source</th>
                      <th className="text-left py-2">Campagne</th>
                      <th className="text-right py-2">Soumissions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {kpiData.submissions_by_utm.map((utm, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="py-2">{utm.utm_source || '-'}</td>
                        <td className="py-2 text-xs" style={{ color: '#666666' }}>{utm.utm_campaign || '-'}</td>
                        <td className="py-2 text-right font-bold">{utm.submissions_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-center py-4" style={{ color: '#999999' }}>
                Aucune soumission avec UTM
              </p>
            )}
          </div>

          {/* Recent Deals */}
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: '#333333' }}>
              <Calendar size={18} />
              Deals Récents
            </h3>
            {kpiData?.recent_deals?.length > 0 ? (
              <div className="space-y-2">
                {kpiData.recent_deals.map(deal => {
                  const stageColors = {
                    lead: '#64B5F6', qualified: '#4DB6AC', estimate: '#FFB74D',
                    negotiation: '#BA68C8', won: '#81C784', production: '#4FC3F7',
                    delivered: '#2E7D32', lost: '#E57373'
                  };
                  
                  return (
                    <div 
                      key={deal.id}
                      className="flex items-center justify-between p-2 rounded hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate('/admin/pipeline')}
                    >
                      <div>
                        <p className="font-medium text-sm">{deal.title || 'Sans titre'}</p>
                        <p className="text-xs" style={{ color: '#999999' }}>
                          {deal.deal_type?.toUpperCase()} | {deal.source || 'N/A'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span 
                          className="px-2 py-0.5 rounded text-xs"
                          style={{ background: stageColors[deal.stage] + '20', color: stageColors[deal.stage] }}
                        >
                          {deal.stage}
                        </span>
                        {deal.estimated_value && (
                          <span className="text-xs font-bold" style={{ color: '#C7A027' }}>
                            {formatCurrency(deal.estimated_value)}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-center py-4" style={{ color: '#999999' }}>
                Aucun deal récent
              </p>
            )}
          </div>
        </div>

        {/* KPI Soumissions & Contrats (Vague 4) */}
        {salesKpi && (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Soumissions KPI */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: '#333333' }}>
                <FileText size={18} style={{ color: '#1976D2' }} />
                KPI Soumissions
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 rounded-lg" style={{ background: '#E3F2FD' }}>
                  <p className="text-xs" style={{ color: '#1976D2' }}>Total</p>
                  <p className="font-bold text-2xl" style={{ color: '#1565C0' }}>{salesKpi.submissions?.total || 0}</p>
                </div>
                <div className="p-3 rounded-lg" style={{ background: '#FFF3E0' }}>
                  <p className="text-xs" style={{ color: '#E65100' }}>Envoyées</p>
                  <p className="font-bold text-2xl" style={{ color: '#EF6C00' }}>{salesKpi.submissions?.sent || 0}</p>
                </div>
                <div className="p-3 rounded-lg" style={{ background: '#E8F5E9' }}>
                  <p className="text-xs" style={{ color: '#2E7D32' }}>Acceptées</p>
                  <p className="font-bold text-2xl" style={{ color: '#388E3C' }}>{salesKpi.submissions?.accepted || 0}</p>
                </div>
                <div className="p-3 rounded-lg" style={{ background: '#FFEBEE' }}>
                  <p className="text-xs" style={{ color: '#C62828' }}>Rejetées</p>
                  <p className="font-bold text-2xl" style={{ color: '#D32F2F' }}>{salesKpi.submissions?.rejected || 0}</p>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                <div className="p-2 rounded" style={{ background: '#F5F5F5' }}>
                  <p className="text-xs" style={{ color: '#666' }}>Taux acceptation</p>
                  <p className="font-bold" style={{ color: salesKpi.submissions?.acceptance_rate >= 30 ? '#2E7D32' : '#E65100' }}>
                    {salesKpi.submissions?.acceptance_rate || 0}%
                  </p>
                </div>
                <div className="p-2 rounded" style={{ background: '#F5F5F5' }}>
                  <p className="text-xs" style={{ color: '#666' }}>Temps moyen</p>
                  <p className="font-bold">{salesKpi.submissions?.avg_time_to_accept_days || 0}j</p>
                </div>
                <div className="p-2 rounded" style={{ background: '#F5F5F5' }}>
                  <p className="text-xs" style={{ color: '#666' }}>Versions moy.</p>
                  <p className="font-bold">{salesKpi.submissions?.avg_versions_to_win || 1}</p>
                </div>
              </div>
            </div>

            {/* Contrats KPI */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: '#333333' }}>
                <Award size={18} style={{ color: '#C7A027' }} />
                KPI Contrats
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 rounded-lg" style={{ background: '#FFF8E1' }}>
                  <p className="text-xs" style={{ color: '#F57F17' }}>Total</p>
                  <p className="font-bold text-2xl" style={{ color: '#F9A825' }}>{salesKpi.contracts?.total || 0}</p>
                </div>
                <div className="p-3 rounded-lg" style={{ background: '#E8F5E9' }}>
                  <p className="text-xs" style={{ color: '#2E7D32' }}>Signés</p>
                  <p className="font-bold text-2xl" style={{ color: '#388E3C' }}>{salesKpi.contracts?.signed || 0}</p>
                </div>
                <div className="p-3 rounded-lg" style={{ background: '#F3E5F5' }}>
                  <p className="text-xs" style={{ color: '#7B1FA2' }}>En production</p>
                  <p className="font-bold text-2xl" style={{ color: '#8E24AA' }}>{salesKpi.contracts?.in_production || 0}</p>
                </div>
                <div className="p-3 rounded-lg" style={{ background: '#E0F2F1' }}>
                  <p className="text-xs" style={{ color: '#00695C' }}>Livrés</p>
                  <p className="font-bold text-2xl" style={{ color: '#00897B' }}>{salesKpi.contracts?.delivered || 0}</p>
                </div>
              </div>
              <div className="mt-4 p-3 rounded-lg text-center" style={{ background: '#FFF8E1' }}>
                <p className="text-xs" style={{ color: '#666' }}>Valeur totale signée</p>
                <p className="font-bold text-2xl" style={{ color: '#C7A027' }}>
                  {formatCurrency(salesKpi.contracts?.signed_value || 0)}
                </p>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-center">
                <div className="p-2 rounded" style={{ background: '#F5F5F5' }}>
                  <p className="text-xs" style={{ color: '#666' }}>Cycle deal→signé</p>
                  <p className="font-bold">{salesKpi.contracts?.avg_cycle_deal_to_signed_days || 0}j</p>
                </div>
                <div className="p-2 rounded" style={{ background: '#F5F5F5' }}>
                  <p className="text-xs" style={{ color: '#666' }}>Cycle signé→livré</p>
                  <p className="font-bold">{salesKpi.contracts?.avg_cycle_signed_to_delivered_days || 0}j</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ROI Marketing par Campagne */}
        {kpiData?.marketing_roi?.campaigns?.length > 0 && (
          <div className="mt-6 bg-white rounded-lg p-4 shadow-sm">
            <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: '#333333' }}>
              <TrendingUp size={18} />
              ROI Marketing par Campagne
            </h3>
            
            {/* Totaux */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-4 p-3 rounded-lg" style={{ background: '#F5F5F5' }}>
              <div className="text-center">
                <p className="text-xs" style={{ color: '#666' }}>Campagnes</p>
                <p className="font-bold text-lg">{kpiData.marketing_roi.totals.campaigns_count}</p>
              </div>
              <div className="text-center">
                <p className="text-xs" style={{ color: '#666' }}>Destinataires</p>
                <p className="font-bold text-lg">{kpiData.marketing_roi.totals.total_recipients}</p>
              </div>
              <div className="text-center">
                <p className="text-xs" style={{ color: '#666' }}>Soumissions</p>
                <p className="font-bold text-lg">{kpiData.marketing_roi.totals.total_submissions}</p>
              </div>
              <div className="text-center">
                <p className="text-xs" style={{ color: '#666' }}>Gagnés</p>
                <p className="font-bold text-lg" style={{ color: '#2E7D32' }}>{kpiData.marketing_roi.totals.total_won}</p>
              </div>
              <div className="text-center">
                <p className="text-xs" style={{ color: '#666' }}>Revenus</p>
                <p className="font-bold text-lg" style={{ color: '#C7A027' }}>{formatCurrency(kpiData.marketing_roi.totals.total_won_value)}</p>
              </div>
            </div>
            
            {/* Tableau détaillé */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b" style={{ background: '#F5F5F5' }}>
                    <th className="text-left py-2 px-2">Campagne</th>
                    <th className="text-right py-2 px-2">Envois</th>
                    <th className="text-right py-2 px-2">Soumissions</th>
                    <th className="text-right py-2 px-2">Deals</th>
                    <th className="text-right py-2 px-2">Gagnés</th>
                    <th className="text-right py-2 px-2">Revenus</th>
                    <th className="text-right py-2 px-2">Conv. %</th>
                  </tr>
                </thead>
                <tbody>
                  {kpiData.marketing_roi.campaigns.slice(0, 10).map((campaign, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50">
                      <td className="py-2 px-2 font-medium">{campaign.campaign_name}</td>
                      <td className="py-2 px-2 text-right">{campaign.recipients_count}</td>
                      <td className="py-2 px-2 text-right">{campaign.submissions_count}</td>
                      <td className="py-2 px-2 text-right">{campaign.deals_count}</td>
                      <td className="py-2 px-2 text-right font-bold" style={{ color: '#2E7D32' }}>{campaign.won_count}</td>
                      <td className="py-2 px-2 text-right font-bold" style={{ color: '#C7A027' }}>{formatCurrency(campaign.won_value)}</td>
                      <td className="py-2 px-2 text-right">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          campaign.conversion_submission_to_won >= 20 ? 'bg-green-100 text-green-800' :
                          campaign.conversion_submission_to_won >= 10 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {campaign.conversion_submission_to_won}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="mt-6 flex flex-wrap gap-3">
          <button
            onClick={() => navigate('/admin/pipeline')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm sm:text-base"
            style={{ background: '#335C2E', color: 'white' }}
          >
            <BarChart3 size={18} />
            <span className="hidden sm:inline">Voir le</span> Pipeline
          </button>
          <button
            onClick={() => navigate('/admin/crm')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm sm:text-base"
            style={{ background: '#1976D2', color: 'white' }}
          >
            <Users size={18} />
            <span className="hidden sm:inline">Gérer les</span> Contacts
          </button>
          <button
            onClick={() => navigate('/admin/campaigns')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm sm:text-base"
            style={{ background: '#C7A027', color: 'white' }}
          >
            <Mail size={18} />
            Campagnes
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
